import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import classNames from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../../services/security-services/SecurityActions";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import { generateStrongPassword } from "../../../securityUtils/passwordUtils";

const schema = yup.object().shape({
  newPassword: yup
    .string()
    .required("Password is required")
    .min(8, "Password is too short (8 chars min)")
    .matches(/[a-z]/, "At least 1 lowercase letter")
    .matches(/[A-Z]/, "At least 1 uppercase letter")
    .matches(/[0-9]/, "At least 1 digit")
    .matches(/[\W]/, "At least 1 special character"),
  confirmPassword: yup
    .string()
    .required("Confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

export default function ResetPasswordForm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFocusedPassword, setHasFocusedPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const newPassword = watch("newPassword") || "";

  const handleSuggestPassword = () => {
    const generated = generateStrongPassword();
    setValue("newPassword", generated, { shouldValidate: true });
    setValue("confirmPassword", generated, { shouldValidate: true });
    setShowPassword1(true);
    setShowPassword2(true);
    setHasFocusedPassword(true);
    navigator.clipboard?.writeText(generated);
    toast.success("Strong password generated and copied to clipboard");
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    const request = {
      password: data.newPassword,
      token: searchParams.get("token"),
    };
    const success = await dispatch(resetPassword(request));
    setIsLoading(false);

    if (success) {
      navigate("/login");
      toast.success(t('resetPasswordForm.messages.passwordReset'));
    } else {
      toast.error(t('resetPasswordForm.messages.linkExpired'));
    }
  };

  const checkPasswordRules = (password) => ({
    minLength: password.length >= 8,
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[\W]/.test(password),
  });

  const rules = checkPasswordRules(newPassword);

  const getStrength = () => {
    const score = Object.values(rules).filter(Boolean).length;
    const colors = ["#dc3545", "#fd7e14", "#ffc107", "#0d6efd", "#198754"];
    const labels = [
      t('resetPasswordForm.passwordStrength.tooWeak'),
      t('resetPasswordForm.passwordStrength.weak'),
      t('resetPasswordForm.passwordStrength.fair'),
      t('resetPasswordForm.passwordStrength.strong'),
      t('resetPasswordForm.passwordStrength.veryStrong')
    ];
    return {
      color: colors[score - 1] || "#ccc",
      label: labels[score - 1] || t('resetPasswordForm.passwordStrength.tooWeak'),
      score,
    };
  };

  const strength = getStrength();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="text-left">

      {/* New password */}
      <div className="form-group mb-4">
        <label htmlFor="newPassword" className="mb-1 fw-medium">{t('resetPasswordForm.newPassword')}</label>
        <div
          className="input-group border rounded shadow-sm overflow-hidden"
          style={{
            transition: "all 0.2s ease-in-out",
            boxShadow: errors.newPassword ? "0 0 0 3px rgba(220, 53, 69, 0.25)" : undefined,
          }}
        >
          <span className="input-group-text bg-white border-0">
            <i className="fa fa-lock" />
          </span>
          <input
            type={showPassword1 ? "text" : "password"}
            id="newPassword"
            placeholder={t('resetPasswordForm.newPasswordPlaceholder')}
            {...register("newPassword")}
            className={classNames("form-control border-0", {
              "is-invalid": errors.newPassword,
            })}
            onFocus={(e) => {
              setHasFocusedPassword(true);
              e.target.parentNode.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.25)";
            }}
            onBlur={(e) =>
              e.target.parentNode.style.boxShadow = errors.newPassword
                ? "0 0 0 3px rgba(220, 53, 69, 0.25)"
                : "none"
            }
          />
          <button
            className="btn bg-white border-0"
            type="button"
            onClick={() => setShowPassword1(!showPassword1)}
          >
            <i className={`fa ${showPassword1 ? "fa-eye" : "fa-eye-slash"}`} />
          </button>
        </div>
        <div className="invalid-feedback d-block">{errors.newPassword?.message}</div>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary mt-2"
          onClick={handleSuggestPassword}
          style={{ fontSize: "0.8rem" }}
        >
          <i className="fas fa-magic me-1" />
          Suggest strong password
        </button>

        {/* Password checklist + Strength */}
        {hasFocusedPassword && (
          <>
            <ul style={{ listStyle: "none", paddingLeft: 0, fontSize: "0.875rem", marginTop: "0.75rem" }}>
              <li style={{ color: rules.minLength ? "#198754" : "#dc3545" }}>
                {rules.minLength ? "✔" : "✖"} {t('resetPasswordForm.passwordRules.minLength')}
              </li>
              <li style={{ color: rules.hasLowercase ? "#198754" : "#dc3545" }}>
                {rules.hasLowercase ? "✔" : "✖"} {t('resetPasswordForm.passwordRules.lowercase')}
              </li>
              <li style={{ color: rules.hasUppercase ? "#198754" : "#dc3545" }}>
                {rules.hasUppercase ? "✔" : "✖"} {t('resetPasswordForm.passwordRules.uppercase')}
              </li>
              <li style={{ color: rules.hasNumber ? "#198754" : "#dc3545" }}>
                {rules.hasNumber ? "✔" : "✖"} {t('resetPasswordForm.passwordRules.number')}
              </li>
              <li style={{ color: rules.hasSpecial ? "#198754" : "#dc3545" }}>
                {rules.hasSpecial ? "✔" : "✖"} {t('resetPasswordForm.passwordRules.special')}
              </li>
            </ul>

            <div className="mt-2">
              <div
                style={{
                  height: "6px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(strength.score / 5) * 100}%`,
                    height: "100%",
                    backgroundColor: strength.color,
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </div>
              <div className="mt-1 small" style={{ color: strength.color }}>
                {strength.label}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirm password */}
      <div className="form-group mb-4">
        <label htmlFor="confirmPassword" className="mb-1 fw-medium">{t('resetPasswordForm.confirmPassword')}</label>
        <div
          className="input-group border rounded shadow-sm overflow-hidden"
          style={{
            transition: "all 0.2s ease-in-out",
            boxShadow: errors.confirmPassword ? "0 0 0 3px rgba(220, 53, 69, 0.25)" : undefined,
          }}
        >
          <span className="input-group-text bg-white border-0">
            <i className="fa fa-lock" />
          </span>
          <input
            type={showPassword2 ? "text" : "password"}
            id="confirmPassword"
            placeholder={t('resetPasswordForm.confirmPasswordPlaceholder')}
            {...register("confirmPassword")}
            className={classNames("form-control border-0", {
              "is-invalid": errors.confirmPassword,
            })}
            onFocus={(e) =>
              e.target.parentNode.style.boxShadow = "0 0 0 3px rgba(124, 58, 237, 0.25)"
            }
            onBlur={(e) =>
              e.target.parentNode.style.boxShadow = errors.confirmPassword
                ? "0 0 0 3px rgba(220, 53, 69, 0.25)"
                : "none"
            }
          />
          <button
            className="btn bg-white border-0"
            type="button"
            onClick={() => setShowPassword2(!showPassword2)}
          >
            <i className={`fa ${showPassword2 ? "fa-eye" : "fa-eye-slash"}`} />
          </button>
        </div>
        <div className="invalid-feedback d-block">{errors.confirmPassword?.message}</div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="btn w-100 py-2 fw-semibold text-white"
        style={{
          background: "linear-gradient(90deg, #7c3aed, #2563eb)",
          border: "none",
          borderRadius: "0.6rem",
          transition: "all 0.2s ease-in-out",
          transform: isLoading ? "scale(0.98)" : "scale(1)",
          boxShadow: isLoading ? "none" : "0 4px 14px rgba(36, 99, 235, 0.25)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 6px 18px rgba(36, 99, 235, 0.35)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 14px rgba(36, 99, 235, 0.25)";
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "scale(0.98)";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            {t('resetPasswordForm.settingPassword')}
          </>
        ) : (
          t('resetPasswordForm.setPassword')
        )}
      </button>
    </form>
  );
}
